import { BEIGE_500, GRAY_200, GRAY_500, GRAY_700, GRAY_900, WHITE } from 'constants/styling/theme'
import { Add, Check, DoneAll } from '@mui/icons-material'

import { ConversationMessageDTO, ConversationMessageFeedback, ConversationMessageSource, ConversationMessageStatus, ConversationMessageType, ConversationParticipantDTO, ConversationParticipantRole, ConversationStatus, ConversationType, CursorPageDTO, MessageAction, MessageFeedbackOutcome } from 'models/conversations'
import { FC, Fragment, useEffect, useMemo, useRef, useState } from 'react'
import { conversationMessagesKey, isConversationClosedError, isRatingConflict, isRatingEffectRefused, isRatingRejectedAsClosed, isRatingRetryTooSoon, responseStatusOf, useFetchMessagesPage, useFetchOlderMessages, useGetMessages, useGetSupportHours, useExecuteMessageAction, useMarkConversationRead, useRateAssistantAnswer, useSendMessage, useUpdateConversationStatus } from 'dataQueries/conversations.query'
import { format, isSameDay, isToday, isYesterday } from 'date-fns'

import { AnalyticsEvent, logAnalyticsEvent } from 'utils/analytics'
import { AIDisclaimer } from '../AIDisclaimer/AIDisclaimer'
import { AnswerFeedback } from './AnswerFeedback'
import { AGENT_BUBBLE_BG, AgentAvatar } from '../AgentAvatar/AgentAvatar'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import { MUIBadge } from 'components/common/MUIBadge'
import { MessageAttachments } from '../Attachments/MessageAttachments'
import { MessageInput } from '../NewConversation/MessageInput'
import { StartConversationButton } from '../NewConversation/StartConversationButton'
import { MessageSuggestions } from './MessageSuggestions'
import { EntityKeys, QueryType } from 'utils/reactQuery'
import { Nullable } from 'models/helpers'
import { SupportClosedNotice } from '../SupportHours/SupportClosedNotice'
import { ReconnectionBanner } from '../ReconnectionBanner/ReconnectionBanner'
import Stack from '@mui/material/Stack'
import { TypingDots } from '../TypingDots/TypingDots'
import { TFunction } from 'i18next'
import Typography from '@mui/material/Typography'
import { logger } from 'utils/logging'
import { markConversationClosedInList } from '../helpers/conversationCache'
import { CONVERSATION_ASSISTANT_ID } from '../helpers/conversationOverview'
import { outsideHoursFallbackReason, reopensAtForNotice } from '../helpers/supportHours'
import { useLoadedConversationLanguage, useSystemNoticeTranslator } from '../helpers/useSystemNoticeTranslator'
import { useChatWidget } from '../ChatWidget.context'
import { useConversationSocket } from 'utils/conversationSocket'
import { toMessageAttachment, useChatAttachments } from '../helpers/useChatAttachments'
import axios from 'axios'
import { fetchMessagesBackTo, useAttachmentUrlRefresh, withFreshAttachmentUrls } from '../helpers/useAttachmentUrlRefresh'
import { useIdempotentClientMessageId } from '../helpers/useIdempotentClientMessageId'
import { useCurrentParticipantId } from '../helpers/useCurrentParticipantId'
import { useQueryClient } from '@tanstack/react-query'
import { useSnackbar } from 'components/contexts/SnackbarService.context'
import { useSupportHoursLocale } from '../SupportHours/useSupportHoursLocale'
import { useSupportHoursNow } from '../SupportHours/useSupportHoursNow'
import { useTranslation } from 'react-i18next'
import { visuallyHidden } from '@mui/utils'

const TYPING_THROTTLE_MS = 2_000
const ASSISTANT_PENDING_TIMEOUT_MS = 30_000
const ASSISTANT_PENDING_CLOCK_SKEW_MS = 5_000
const SCROLL_TOP_THRESHOLD_PX = 48
/** Within this distance of the end the client is reading the latest messages, and the thread follows arrivals. */
const SCROLL_BOTTOM_THRESHOLD_PX = 80

const freshWait = (since: number | undefined): number | null =>
  since && Date.now() - since < ASSISTANT_PENDING_TIMEOUT_MS ? since : null

const isAgentHandover = (previousName: string | null, name: string | null): boolean =>
  previousName !== null && name !== null && previousName !== name

const READ_TICK_COLOR = '#4FC3F7'
const ESCALATION_INCOMING_SYSTEM_CODE = 'escalation_agent_incoming'
const AGENT_JOINED_SYSTEM_CODE = 'agent_joined'
const AWAITING_AGENT_OUTSIDE_HOURS_SYSTEM_CODE = 'awaiting_agent_outside_hours'
const ACTION_MESSAGE_ID_PREFIX = 'assistant-action-'

const reportedNoticeFallbacks = new Set<string>()
const reportedUntranslatedCodes = new Set<string>()
const reportedAttachmentsWithoutUrl = new Set<string>()

type SenderKind = 'self' | 'assistant' | 'agent' | 'other' | 'system'
type AssistantWaitOutcome = 'answered' | 'agent_joined' | 'timed_out'

const dayLabel = (iso: string, t: TFunction): string => {
  const date = new Date(iso)
  if (isToday(date)) return t('chat:thread.date.today')
  if (isYesterday(date)) return t('chat:thread.date.yesterday')
  return format(date, 'd MMMM yyyy')
}

const renderOutgoingStatus = (status: ConversationMessageStatus) => {
  switch (status) {
    case ConversationMessageStatus.SENT:
      return <Check sx={{ fontSize: '1.4rem', color: 'rgba(255,255,255,0.7)' }} />
    case ConversationMessageStatus.DELIVERED:
      return <DoneAll sx={{ fontSize: '1.4rem', color: 'rgba(255,255,255,0.7)' }} />
    case ConversationMessageStatus.READ:
      return <DoneAll sx={{ fontSize: '1.4rem', color: READ_TICK_COLOR }} />
  }
}

interface Props {
  conversationId: string
  conversationType?: ConversationType
  awaitingAssistantSince?: number
  currentUserId?: string
  compact?: boolean
  readOnly?: boolean
  conversationStatus?: ConversationStatus
  conversationLanguage?: Nullable<string>
  participants?: ConversationParticipantDTO[]
  reopenedAt?: Nullable<string>
}

export const ConversationThread: FC<Props> = ({ conversationId, conversationType, awaitingAssistantSince, currentUserId: currentUserIdProp, compact = false, readOnly = false, conversationStatus, conversationLanguage, participants, reopenedAt }) => {
  const { t } = useTranslation(['chat'])
  const systemNoticeT = useSystemNoticeTranslator(conversationLanguage)
  const uiLocale = useSupportHoursLocale()
  const noticeLocale = useLoadedConversationLanguage(conversationLanguage) ?? uiLocale
  const isSupportConversation = conversationType === ConversationType.AGENT_CLIENT || conversationType === ConversationType.AI_SUPPORT
  const { data: supportHours } = useGetSupportHours(isSupportConversation)
  const now = useSupportHoursNow(supportHours)
  const { spawnErrorToast } = useSnackbar()
  const queryClient = useQueryClient()
  const { data: page, isLoading } = useGetMessages(conversationId)
  const sendMessage = useSendMessage()
  const updateStatus = useUpdateConversationStatus()
  const executeAction = useExecuteMessageAction()
  const rateAnswer = useRateAssistantAnswer()
  const markRead = useMarkConversationRead()
  const { sendTyping } = useConversationSocket()
  const { typingConversationIds, typingUserIds, selectConversation, registerRenderedThread, isDocumentVisible, startNewConversation } = useChatWidget()
  const fetchOlder = useFetchOlderMessages()
  const attachments = useChatAttachments({ conversationId, conversationType })
  const clientMessageIds = useIdempotentClientMessageId()
  const fetchMessagesPage = useFetchMessagesPage()
  const pendingSendRef = useRef<{ clientMessageId: string, keys: string[] } | null>(null)
  const currentConversationIdRef = useRef(conversationId)
  currentConversationIdRef.current = conversationId
  const scrollAnchorRef = useRef<HTMLDivElement>(null)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const isPinnedToBottomRef = useRef(true)
  const lastMarkedReadRef = useRef<string | null>(null)
  const lastTypingSentRef = useRef(0)
  const lastTailIdRef = useRef<string | null>(null)
  const pendingAfterMessageId = useRef<string | null>(null)
  const [olderMessages, setOlderMessages] = useState<ConversationMessageDTO[]>([])
  const [hasMoreOlder, setHasMoreOlder] = useState(true)
  const [isLoadingOlder, setIsLoadingOlder] = useState(false)
  const [closePromptDismissedId, setClosePromptDismissedId] = useState<string | null>(null)
  const [closedConversationId, setClosedConversationId] = useState<string | null>(null)
  const [selectedActions, setSelectedActions] = useState<Record<string, string>>({})
  const [pendingRatings, setPendingRatings] = useState<Record<string, ConversationMessageFeedback>>({})
  const [failedRatings, setFailedRatings] = useState<Record<string, ConversationMessageFeedback>>({})

  useEffect(() => registerRenderedThread(conversationId), [registerRenderedThread, conversationId])

  useEffect(() => {
    setOlderMessages([])
    pendingSendRef.current = null
    setHasMoreOlder(true)
    setClosePromptDismissedId(null)
    setSelectedActions({})
    setPendingRatings({})
    setFailedRatings({})
    lastMarkedReadRef.current = null
    isPinnedToBottomRef.current = true
  }, [conversationId])

  const lastKnownStatusRef = useRef(new Map<string, ConversationStatus>())
  useEffect(() => {
    if (conversationStatus === undefined) return
    const previous = lastKnownStatusRef.current.get(conversationId)
    lastKnownStatusRef.current.set(conversationId, conversationStatus)
    if (previous === ConversationStatus.CLOSED && conversationStatus === ConversationStatus.ESCALATED) {
      setClosedConversationId((closedId) => closedId === conversationId ? null : closedId)
    }
  }, [conversationId, conversationStatus])

  const isReadOnly = readOnly || closedConversationId === conversationId

  const derivedUserId = useCurrentParticipantId(conversationId)
  const currentUserId = derivedUserId ?? currentUserIdProp

  const messages = useMemo(() => {
    const byId = new Map<string, ConversationMessageDTO>()
    for (const message of olderMessages) byId.set(message.id, message)
    for (const message of page?.items ?? []) byId.set(message.id, message)
    return [...byId.values()].sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
  }, [page, olderMessages])

  const latestMessagesRef = useRef(messages)
  latestMessagesRef.current = messages

  const refetchAttachmentUrls = async (): Promise<ConversationMessageDTO[]> => {
    const refreshedConversationId = conversationId
    const oldestWithUrls = messages.find((message) =>
      message.id !== message.clientMessageId && !!message.attachments?.some((attachment) => !!attachment.url))
    if (!oldestWithUrls) return []
    const fetched = await fetchMessagesBackTo((cursor) => fetchMessagesPage(refreshedConversationId, cursor), oldestWithUrls.id)
    if (currentConversationIdRef.current === refreshedConversationId) {
      const refresh = withFreshAttachmentUrls(fetched)
      queryClient.setQueryData<CursorPageDTO<ConversationMessageDTO>>(conversationMessagesKey(refreshedConversationId), (cached) => {
        if (!cached) return cached
        const items = cached.items.map(refresh)
        return items.every((item, index) => item === cached.items[index]) ? cached : { ...cached, items }
      })
      setOlderMessages((previous) => previous.map(refresh))
    }
    return fetched
  }

  const attachmentUrls = useAttachmentUrlRefresh({ conversationId, messages, refetch: refetchAttachmentUrls })

  const settleEchoedSend = (candidates: ConversationMessageDTO[]): boolean => {
    const pending = pendingSendRef.current
    if (!pending) return false
    if (!candidates.some((message) => message.clientMessageId === pending.clientMessageId && message.id !== message.clientMessageId)) return false
    pendingSendRef.current = null
    if (pending.keys.length) attachments.clear(pending.keys)
    clientMessageIds.release()
    return true
  }
  const settleEchoedSendRef = useRef(settleEchoedSend)
  settleEchoedSendRef.current = settleEchoedSend

  useEffect(() => {
    settleEchoedSendRef.current(messages)
  }, [messages])

  useEffect(() => {
    messages.forEach((message) => {
      if (message.id === message.clientMessageId) return
      message.attachments?.forEach((attachment) => {
        if (attachment.url !== null || reportedAttachmentsWithoutUrl.has(attachment.id)) return
        reportedAttachmentsWithoutUrl.add(attachment.id)
        logger.warn('ConversationThread: attachment delivered without a URL', undefined, {
          context: { component: 'ConversationThread', action: 'renderAttachments', metadata: { conversationId, messageId: message.id, attachmentId: attachment.id } },
        })
      })
    })
  }, [messages, conversationId])

  const executedActionValues = useMemo(
    () => new Set(
      messages
        .filter((message) => message.clientMessageId?.startsWith(ACTION_MESSAGE_ID_PREFIX))
        .map((message) => message.clientMessageId.slice(ACTION_MESSAGE_ID_PREFIX.length))
    ),
    [messages]
  )

  // A frame later, so the scroll sees the layout the commit produced, and instantly: a smooth scroll that a
  // following layout change interrupts stops half way, with the newest message still below the fold.
  const scrollToBottom = () => {
    requestAnimationFrame(() => scrollAnchorRef.current?.scrollIntoView({ block: 'end' }))
  }

  useEffect(() => {
    if (isLoading) return
    lastTailIdRef.current = messages[messages.length - 1]?.id ?? null
    isPinnedToBottomRef.current = true
    scrollToBottom()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, isLoading])

  useEffect(() => {
    const tail = messages[messages.length - 1]
    if (!tail || tail.id === lastTailIdRef.current) return
    lastTailIdRef.current = tail.id
    // Follow the thread when the client is reading its end or has just spoken. A client who scrolled up into
    // the history is reading, and an arrival must not yank them back down.
    if (isPinnedToBottomRef.current || tail.senderId === currentUserId) scrollToBottom()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages])

  const loadOlder = async () => {
    if (isLoadingOlder || !hasMoreOlder) return
    const oldest = messages[0]
    const container = scrollContainerRef.current
    if (!oldest || !container) return

    const previousHeight = container.scrollHeight
    setIsLoadingOlder(true)
    try {
      const olderPage = await fetchOlder(conversationId, oldest.id)
      setOlderMessages((prev) => [...prev, ...olderPage.items])
      setHasMoreOlder(olderPage.hasMore)
      requestAnimationFrame(() => {
        const current = scrollContainerRef.current
        if (current) current.scrollTop += current.scrollHeight - previousHeight
      })
    } catch (error) {
      logger.error('ConversationThread: failed to load older messages', error, {
        context: { component: 'ConversationThread', metadata: { conversationId } },
      })
    } finally {
      setIsLoadingOlder(false)
    }
  }

  const handleScroll = () => {
    const container = scrollContainerRef.current
    if (!container) return
    const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight
    isPinnedToBottomRef.current = distanceFromBottom <= SCROLL_BOTTOM_THRESHOLD_PX
    if (container.scrollTop <= SCROLL_TOP_THRESHOLD_PX) void loadOlder()
  }

  // A message that lands while the tab is hidden has not been read: leave it unread (so the badge and the
  // desktop notification fire) and mark it read once the client comes back to the tab.
  useEffect(() => {
    if (!currentUserId) return
    if (!isDocumentVisible) return
    const latest = messages[messages.length - 1] as ConversationMessageDTO | undefined
    if (!latest) return
    if (latest.senderId === currentUserId) return
    if (lastMarkedReadRef.current === latest.id) return
    lastMarkedReadRef.current = latest.id
    markRead.mutate({ conversationId, lastReadMessageId: latest.id })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, conversationId, currentUserId, isDocumentVisible])

  const patchThreadItems = (mutate: (items: ConversationMessageDTO[]) => ConversationMessageDTO[]) => {
    queryClient.setQueryData<CursorPageDTO<ConversationMessageDTO>>(
      conversationMessagesKey(conversationId),
      (cached) => {
        if (!cached) return cached
        const items = mutate(cached.items)
        return { ...cached, items, size: items.length }
      }
    )
  }

  const isOtherTyping = typingConversationIds.has(conversationId)

  const isAssistantConversation = conversationType === ConversationType.AI_SUPPORT
  const isAssistantMessage = (message: ConversationMessageDTO): boolean =>
    isAssistantConversation && message.senderRole === ConversationParticipantRole.AGENT &&
    message.source === ConversationMessageSource.PLATFORM
  const isAgentAnswer = (message: ConversationMessageDTO): boolean =>
    isAssistantConversation && message.senderRole === ConversationParticipantRole.AGENT
  const isHumanAgentMessage = (message: ConversationMessageDTO): boolean =>
    message.source === ConversationMessageSource.SLACK && message.senderId !== currentUserId
  const senderKindOf = (message: ConversationMessageDTO): SenderKind => {
    if (message.type === ConversationMessageType.SYSTEM) return 'system'
    if (currentUserId && message.senderId === currentUserId) return 'self'
    if (isAssistantMessage(message)) return 'assistant'
    if (isHumanAgentMessage(message)) return 'agent'
    return 'other'
  }

  const latestMessage = messages[messages.length - 1]

  const isEscalated = messages.some((m) =>
    m.type === ConversationMessageType.SYSTEM && m.content === ESCALATION_INCOMING_SYSTEM_CODE
  )
  const isHandedToHuman = isEscalated || conversationStatus === ConversationStatus.ESCALATED || messages.some(isHumanAgentMessage)
  const humanAgentParticipants = (participants ?? []).filter((p) =>
    p.role === ConversationParticipantRole.AGENT && p.userId !== CONVERSATION_ASSISTANT_ID
  )
  const hasHumanAgentJoined = humanAgentParticipants.length
    ? humanAgentParticipants.some((p) => !p.leftAt)
    : messages.some((m) =>
      isHumanAgentMessage(m) || (m.type === ConversationMessageType.SYSTEM && m.content === AGENT_JOINED_SYSTEM_CODE)
    )
  const isWaitingForHumanAgent = !hasHumanAgentJoined && (conversationType === ConversationType.AGENT_CLIENT ||
    isEscalated || conversationStatus === ConversationStatus.ESCALATED)

  const assistantIds = new Set(messages.filter(isAssistantMessage).map((m) => m.senderId))
  const [assistantPendingSince, setAssistantPendingSince] = useState<number | null>(() => freshWait(awaitingAssistantSince))
  const assistantPendingSinceRef = useRef(assistantPendingSince)
  const isAwaitingAssistant = assistantPendingSince !== null && isAssistantConversation && !hasHumanAgentJoined
  const lastAgentName = [...messages].reverse().find((m) => isHumanAgentMessage(m) && !!m.senderName?.trim())?.senderName
  const typerId = isOtherTyping ? typingUserIds.get(conversationId) : undefined
  const isAssistantTyping = !!typerId && (assistantIds.has(typerId) || (assistantIds.size === 0 && !isHandedToHuman))
  const typingKind: SenderKind = !isAssistantConversation
    ? 'other'
    : isOtherTyping && !isAssistantTyping ? 'agent' : 'assistant'
  const typingAgentName = participants?.find((p) => p.userId === typerId)?.name?.trim() || lastAgentName
  const isTypingShown = !isReadOnly && (isOtherTyping || isAwaitingAssistant)
  const typingStatusText = !isTypingShown
    ? ''
    : typingKind === 'assistant' ? t('chat:thread.assistant_answering') : t('chat:thread.typing')

  const setAssistantWait = (since: number | null) => {
    assistantPendingSinceRef.current = since
    setAssistantPendingSince(since)
  }

  const endAssistantWait = (outcome: AssistantWaitOutcome) => {
    const since = assistantPendingSinceRef.current
    if (since === null) return
    pendingAfterMessageId.current = null
    setAssistantWait(null)
    if (!isAssistantConversation) return
    const waitedMs = Date.now() - since
    const escalated = isEscalated || conversationStatus === ConversationStatus.ESCALATED
    logAnalyticsEvent(AnalyticsEvent.CHAT_WIDGET_ASSISTANT_WAIT_ENDED, { outcome, waitedMs, escalated })
    if (outcome === 'timed_out') {
      logger.warn('ConversationThread: assistant wait timed out without an answer', undefined, {
        context: { component: 'ConversationThread', action: 'assistantWait', metadata: { conversationId, waitedMs, escalated } },
      })
    }
  }
  const endAssistantWaitRef = useRef(endAssistantWait)
  endAssistantWaitRef.current = endAssistantWait

  const loggedMissingAgentNameRef = useRef<string | null>(null)
  useEffect(() => {
    if (loggedMissingAgentNameRef.current === conversationId) return
    const unnamed = messages.find((m) => !m.senderName?.trim() && (
      (m.type === ConversationMessageType.SYSTEM && m.content === AGENT_JOINED_SYSTEM_CODE) ||
      (m.type !== ConversationMessageType.SYSTEM && isHumanAgentMessage(m))
    ))
    if (!unnamed) return
    loggedMissingAgentNameRef.current = conversationId
    logger.warn('ConversationThread: agent message without senderName', undefined, {
      context: { component: 'ConversationThread', action: 'resolveAgentName', metadata: { conversationId, messageId: unnamed.id } },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, conversationId])

  const systemNoticeText = (message: ConversationMessageDTO): string => {
    if (message.content === AWAITING_AGENT_OUTSIDE_HOURS_SYSTEM_CODE) {
      const reopensAt = reopensAtForNotice(supportHours, message.createdAt, noticeLocale, now)
      return reopensAt
        ? systemNoticeT(`chat:thread.system.${message.content}`, { reopensAt })
        : systemNoticeT(`chat:thread.system_fallback.${message.content}`)
    }
    if (message.content === AGENT_JOINED_SYSTEM_CODE && message.senderName) {
      return systemNoticeT('chat:thread.system.agent_joined_named', { name: message.senderName })
    }
    return systemNoticeT(`chat:thread.system.${message.content}`, { defaultValue: message.content })
  }

  useEffect(() => {
    for (const message of messages) {
      if (message.type !== ConversationMessageType.SYSTEM) continue
      if (message.content === AWAITING_AGENT_OUTSIDE_HOURS_SYSTEM_CODE) {
        const reason = outsideHoursFallbackReason(supportHours, message.createdAt, now)
        const fallbackKey = `${message.id}:${reason}`
        if (!reason || reportedNoticeFallbacks.has(fallbackKey)) continue
        reportedNoticeFallbacks.add(fallbackKey)
        const log = reason === 'no_hours' || reason === 'unreadable' ? logger.warn : logger.info
        log.call(logger, 'ConversationThread: outside-hours notice shown without a reopening time', undefined, {
          context: { component: 'ConversationThread', action: 'systemNoticeText', metadata: { conversationId, messageId: message.id, reason } },
        })
        continue
      }
      if (reportedUntranslatedCodes.has(message.content)) continue
      if (systemNoticeT(`chat:thread.system.${message.content}`, { defaultValue: message.content }) !== message.content) continue
      reportedUntranslatedCodes.add(message.content)
      logger.warn('ConversationThread: system notice has no translation, showing its raw code', undefined, {
        context: { component: 'ConversationThread', action: 'systemNoticeText', metadata: { conversationId, code: message.content } },
      })
    }
  }, [messages, supportHours, now, conversationId, systemNoticeT])

  // The typing row sits under the messages and takes its height from them: when it appears or goes, re-pin
  // so the last lines do not slide under it.
  useEffect(() => {
    if (isPinnedToBottomRef.current) scrollToBottom()
  }, [isOtherTyping, isAwaitingAssistant])

  useEffect(() => {
    if (awaitingAssistantSince) setAssistantWait(freshWait(awaitingAssistantSince))
  }, [awaitingAssistantSince])

  useEffect(() => {
    if (assistantPendingSince === null) return
    const remaining = ASSISTANT_PENDING_TIMEOUT_MS - (Date.now() - assistantPendingSince)
    if (remaining <= 0) {
      endAssistantWaitRef.current('timed_out')
      return
    }
    const timer = window.setTimeout(() => endAssistantWaitRef.current('timed_out'), remaining)
    return () => window.clearTimeout(timer)
  }, [assistantPendingSince])

  useEffect(() => {
    if (assistantPendingSince === null || !latestMessage) return
    if (!isAgentAnswer(latestMessage)) return
    if (latestMessage.id === pendingAfterMessageId.current) return
    if (new Date(latestMessage.createdAt).getTime() + ASSISTANT_PENDING_CLOCK_SKEW_MS < assistantPendingSince) return
    endAssistantWaitRef.current('answered')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [latestMessage, assistantPendingSince, isAssistantConversation])

  useEffect(() => {
    if (assistantPendingSince === null || !hasHumanAgentJoined) return
    endAssistantWaitRef.current('agent_joined')
  }, [assistantPendingSince, hasHumanAgentJoined])

  useEffect(() => {
    pendingAfterMessageId.current = null
    setAssistantWait(freshWait(awaitingAssistantSince))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId])

  const handleTyping = () => {
    const now = Date.now()
    if (now - lastTypingSentRef.current < TYPING_THROTTLE_MS) return
    lastTypingSentRef.current = now
    sendTyping(conversationId, true)
  }

  const lockClosedFromServer = () => {
    setClosedConversationId(conversationId)
    const listFilter = { queryKey: [EntityKeys.CONVERSATION, QueryType.LIST] }
    queryClient.setQueriesData(listFilter, markConversationClosedInList(conversationId, new Date().toISOString()))
    void queryClient.invalidateQueries(listFilter)
  }

  const markClosedFromServer = () => {
    lockClosedFromServer()
    logAnalyticsEvent(AnalyticsEvent.CHAT_WIDGET_SEND_REJECTED_CONVERSATION_CLOSED, { conversationType: conversationType ?? null })
    logger.info('ConversationThread: send rejected, the conversation was closed elsewhere', undefined, {
      context: { component: 'ConversationThread', action: 'handleSubmit', metadata: { conversationId } },
    })
  }

  const handleAttachmentImageLoad = () => {
    if (isPinnedToBottomRef.current) scrollToBottom()
  }

  const handleSubmit = async (text: string): Promise<boolean> => {
    const sentAttachments = attachments.items
    const attachmentIds = attachments.uploadedIds()
    const clientMessageId = clientMessageIds.idFor(conversationId, text, attachmentIds)
    sendTyping(conversationId, false)

    if (currentUserId) {
      const now = new Date().toISOString()
      const optimistic: ConversationMessageDTO = {
        id: clientMessageId,
        conversationId,
        senderId: currentUserId,
        content: text,
        clientMessageId,
        type: text || !attachmentIds.length ? ConversationMessageType.TEXT : ConversationMessageType.ATTACHMENT,
        status: ConversationMessageStatus.SENT,
        source: ConversationMessageSource.PLATFORM,
        createdAt: now,
        updatedAt: now,
        ...(sentAttachments.length > 0 && { attachments: sentAttachments.map(toMessageAttachment) }),
      }
      patchThreadItems((items) => items.some((m) => m.clientMessageId === clientMessageId) ? items : [...items, optimistic])
    }

    try {
      await sendMessage.mutateAsync({
        conversationId,
        content: text,
        clientMessageId,
        ...(attachmentIds.length > 0 && { attachmentIds }),
      })
      clientMessageIds.release()
      pendingSendRef.current = null
      if (attachmentIds.length > 0) {
        attachments.clear(sentAttachments.map(({ key }) => key))
        logAnalyticsEvent(AnalyticsEvent.CHAT_WIDGET_ATTACHMENT_SENT, {
          conversationType: conversationType ?? null,
          count: attachmentIds.length,
          surface: 'thread',
          fileOnly: !text,
        })
      }
      if (isAssistantConversation && !hasHumanAgentJoined && !reopenedAt) setAssistantWait(Date.now())
      return true
    } catch (error) {
      patchThreadItems((items) => items.filter((m) => !(m.clientMessageId === clientMessageId && m.id === clientMessageId)))
      if (isConversationClosedError(error)) {
        markClosedFromServer()
        return false
      }
      pendingSendRef.current = { clientMessageId, keys: sentAttachments.map(({ key }) => key) }
      if (settleEchoedSend(latestMessagesRef.current)) return true
      const status = axios.isAxiosError(error) ? error.response?.status : undefined
      logger.error('ConversationThread: send failed', error, {
        context: {
          component: 'ConversationThread',
          action: 'handleSubmit',
          metadata: { conversationId, step: 'send', status, attachmentCount: attachmentIds.length, attachmentIds },
        },
      })
      if (attachmentIds.length > 0) {
        logAnalyticsEvent(AnalyticsEvent.CHAT_WIDGET_ATTACHMENT_UPLOAD_FAILED, {
          conversationType: conversationType ?? null,
          step: 'send',
          count: attachmentIds.length,
        })
      }
      throw error
    }
  }

  const handleSelectSuggestion = async (messageId: string, action: MessageAction) => {
    if (selectedActions[messageId] || executedActionValues.has(action.value)) return
    setSelectedActions((prev) => ({ ...prev, [messageId]: action.value }))
    const releasePicker = () => setSelectedActions((prev) => {
      const { [messageId]: _dropped, ...rest } = prev
      return rest
    })
    try {
      const { data } = await executeAction.mutateAsync({ conversationId, messageId, actionId: action.value })
      if (data.result === 'CONVERSATION_CREATED' && data.conversationId) {
        selectConversation(data.conversationId)
        return
      }
      if (data.result !== 'ASSISTANT_TURN') {
        throw new Error(`Unexpected action result: ${String(data.result)}`)
      }
      pendingAfterMessageId.current = latestMessage?.id ?? null
      setAssistantWait(Date.now())
    } catch (error) {
      releasePicker()
      spawnErrorToast(t('chat:errors.action_failed'))
      logger.error('ConversationThread: action execution failed', error, {
        context: { component: 'ConversationThread', action: 'handleSelectSuggestion', metadata: { conversationId, messageId } },
      })
    }
  }

  const handleClosePrompt = (messageId: string, value: string) => {
    setClosePromptDismissedId(messageId)
    if (value !== 'yes') return
    setClosedConversationId(conversationId)
    updateStatus.mutate(
      { conversationId, status: ConversationStatus.CLOSED },
      {
        onSuccess: () => {
          logAnalyticsEvent(AnalyticsEvent.CHAT_WIDGET_CONVERSATION_CLOSED, { conversationType: conversationType ?? null, source: 'close_prompt' })
        },
        onError: (error) => {
          setClosedConversationId(null)
          setClosePromptDismissedId(null)
          logger.error('Failed to close conversation from the prompt', error, {
            context: { component: 'ConversationThread', action: 'handleClosePrompt', metadata: { conversationId } },
          })
        },
      }
    )
  }

  const dropRating = (setter: typeof setPendingRatings, messageId: string) => setter((prev) => {
    const { [messageId]: _dropped, ...rest } = prev
    return rest
  })

  const handleChooseRating = (messageId: string, rating: ConversationMessageFeedback) => {
    logAnalyticsEvent(AnalyticsEvent.CHAT_WIDGET_ASSISTANT_ANSWER_THUMB_TAPPED, { conversationId, messageId, rating })
    scrollToBottom()
  }

  const handleRateAnswer = async (messageId: string, rating: ConversationMessageFeedback, confirmed: boolean) => {
    if (rateAnswer.isPending || pendingRatings[messageId]) return
    const context = { component: 'ConversationThread', action: 'handleRateAnswer', metadata: { conversationId, messageId, rating, confirmed } }
    setPendingRatings((prev) => ({ ...prev, [messageId]: rating }))
    dropRating(setFailedRatings, messageId)
    try {
      const { data } = await rateAnswer.mutateAsync({ conversationId, messageId, rating, confirmed })
      logAnalyticsEvent(AnalyticsEvent.CHAT_WIDGET_ASSISTANT_ANSWER_RATED, { conversationId, messageId, rating: data.rating, outcome: data.outcome, confirmed })
      if (data.outcome === MessageFeedbackOutcome.ALREADY_ESCALATED) {
        void queryClient.invalidateQueries({ queryKey: conversationMessagesKey(conversationId) })
        logger.warn('ConversationThread: rated an answer of a conversation the thread did not know was escalated', undefined, { context })
        return
      }
      if (data.outcome === MessageFeedbackOutcome.ESCALATED) {
        void queryClient.invalidateQueries({ queryKey: conversationMessagesKey(conversationId) })
        return
      }
      if (data.outcome !== MessageFeedbackOutcome.CLOSED) return
      setClosedConversationId(conversationId)
      logAnalyticsEvent(AnalyticsEvent.CHAT_WIDGET_CONVERSATION_CLOSED, { conversationType: conversationType ?? null, source: 'answer_feedback' })
    } catch (error) {
      const status = responseStatusOf(error)
      const statusContext = { ...context, metadata: { ...context.metadata, status } }
      void queryClient.invalidateQueries({ queryKey: conversationMessagesKey(conversationId) })
      logAnalyticsEvent(AnalyticsEvent.CHAT_WIDGET_ASSISTANT_ANSWER_RATED, { conversationId, messageId, rating, outcome: 'FAILED', status, confirmed })
      if (isRatingEffectRefused(error)) {
        spawnErrorToast(t('chat:errors.feedback_handoff_unavailable'))
        logger.error('Failed to rate the assistant answer: its effect was refused for good', error, { context: statusContext })
        return
      }
      dropRating(setPendingRatings, messageId)
      if (isRatingRejectedAsClosed(error)) {
        lockClosedFromServer()
        logger.warn('ConversationThread: rating rejected, the conversation was closed elsewhere', undefined, { context })
        return
      }
      if (isRatingConflict(error)) {
        logger.warn('ConversationThread: rating rejected, the answer was already rated', undefined, { context: statusContext })
        return
      }
      if (confirmed) {
        setFailedRatings((prev) => ({ ...prev, [messageId]: rating }))
        scrollToBottom()
      }
      spawnErrorToast(t('chat:errors.feedback_failed'))
      if (isRatingRetryTooSoon(error)) {
        logger.warn('ConversationThread: rating retried too soon after the previous attempt', undefined, { context: statusContext })
        return
      }
      logger.error('Failed to rate the assistant answer', error, { context: statusContext })
    }
  }

  const agentRunNames = new Map<string, string | null>()
  messages.reduce<string | null>((carried, message) => {
    if (senderKindOf(message) !== 'agent') return null
    const name = message.senderName?.trim() || carried
    agentRunNames.set(message.id, name)
    return name
  }, null)

  const areSuggestionsLive = !isReadOnly && !isEscalated &&
    (conversationStatus === undefined || conversationStatus === ConversationStatus.ACTIVE)

  const showClosePrompt = isAssistantConversation && !isEscalated && !isReadOnly && !isAwaitingAssistant &&
    !!latestMessage && isAssistantMessage(latestMessage) && latestMessage.closePrompt === true &&
    closePromptDismissedId !== latestMessage.id

  const ratingOf = (message: ConversationMessageDTO): Nullable<ConversationMessageFeedback> =>
    pendingRatings[message.id] ?? message.feedback

  const isAnswerRatingLive = isAssistantConversation && !isReadOnly && !isHandedToHuman &&
    (conversationStatus === undefined || conversationStatus === ConversationStatus.ACTIVE)
  const retryRatingOf = (message: ConversationMessageDTO): Nullable<ConversationMessageFeedback> =>
    isAnswerRatingLive && !pendingRatings[message.id] ? failedRatings[message.id] : null

  const latestAnswer = [...messages].reverse().find((m) => m.type !== ConversationMessageType.SYSTEM)
  const rateableAnswerId = isAnswerRatingLive && !isAwaitingAssistant && !showClosePrompt &&
    !!latestAnswer && isAssistantMessage(latestAnswer) && !ratingOf(latestAnswer)
    ? latestAnswer.id
    : null

  return (
    <Stack height="100%" minHeight={0} sx={{ backgroundColor: WHITE }}>

      <ReconnectionBanner />

      <Box role="status" sx={visuallyHidden}>
        {typingStatusText}
      </Box>

      <Box
        ref={scrollContainerRef}
        data-testid="conversation-thread-messages"
        onScroll={handleScroll}
        flex={1}
        minHeight={0}
        sx={{ overflowY: 'auto', padding: compact ? '1.2rem 1.6rem' : '2rem 2.4rem' }}
      >
        {isLoadingOlder &&
          <Stack alignItems="center" sx={{ paddingBottom: '0.8rem' }}>
            <CircularProgress size={18} />
          </Stack>
        }

        {isLoading
          ? <Stack alignItems="center" justifyContent="center" height="100%">
            <CircularProgress size={24} />
          </Stack>
          : messages.length === 0 && !isTypingShown
            ? <Stack alignItems="center" justifyContent="center" height="100%">
              <Typography variant="text-md" color={GRAY_500}>
                {t('chat:thread.empty')}
              </Typography>
            </Stack>
            : <Stack gap="0.8rem">
              {messages.map((message, index) => {
                const agentName = agentRunNames.get(message.id) ?? null
                const showDate = index === 0 ||
                  !isSameDay(new Date(messages[index - 1].createdAt), new Date(message.createdAt))
                const dateSeparator = showDate
                  ? <Stack alignItems="center" sx={{ padding: '0.6rem 0' }}>
                    <Typography variant="text-xs" sx={{ color: GRAY_500, fontWeight: 600 }}>
                      {dayLabel(message.createdAt, t)}
                    </Typography>
                  </Stack>
                  : null

                if (message.type === ConversationMessageType.SYSTEM) {
                  return (
                    <Fragment key={message.id}>

                      {dateSeparator}

                      <Stack alignItems="center" sx={{ padding: '0.4rem 0' }}>
                        <Typography
                          variant="text-xs"
                          sx={{ color: GRAY_500, textAlign: 'center', fontStyle: 'italic' }}
                        >
                          {systemNoticeText(message)}
                        </Typography>
                      </Stack>

                    </Fragment>
                  )
                }

                const senderKind = senderKindOf(message)
                const isOutgoing = senderKind === 'self'
                const isAssistant = senderKind === 'assistant'
                const isAgent = isAssistant || senderKind === 'agent'
                const senderLabel = isAssistant
                  ? t('chat:conversation.assistant_title')
                  : agentName || t('chat:thread.agent_badge')
                const previous = messages[index - 1] as ConversationMessageDTO | undefined
                const startsSenderRun = showDate || !previous || senderKindOf(previous) !== senderKind ||
                  (senderKind === 'agent' && isAgentHandover(agentRunNames.get(previous.id) ?? null, agentName))
                const capturedByAssistant = isAssistantConversation && !!isOutgoing &&
                  (messages.slice(index + 1).some((m) => isAssistantMessage(m)) ||
                    (index === messages.length - 1 && isAwaitingAssistant))
                return (
                  <Fragment key={message.id}>

                    {dateSeparator}

                    <Stack
                      direction="row"
                      alignItems="flex-start"
                      gap="0.8rem"
                      justifyContent={isOutgoing ? 'flex-end' : 'flex-start'}
                    >

                      {!isOutgoing &&
                        (isAgent
                          ? <AgentAvatar kind={isAssistant ? 'assistant' : 'agent'} name={agentName} />
                          : <Box sx={{ width: '3.4rem', flexShrink: 0 }} />)
                      }

                      <Stack
                        gap="0.4rem"
                        alignItems={isOutgoing ? 'flex-end' : 'flex-start'}
                        sx={{ maxWidth: '78%', minWidth: 0 }}
                      >

                        {isAgent && startsSenderRun &&
                          <Stack direction="row" alignItems="center" gap="0.6rem">

                            <Typography variant="text-xs" sx={{ color: GRAY_700, fontWeight: 600 }}>
                              {senderLabel}
                            </Typography>

                            {isAssistant &&
                              <MUIBadge label={t('chat:thread.ai_badge')} size="xs" color="blue" />
                            }

                          </Stack>
                        }

                        <Stack
                          sx={{
                            maxWidth: '100%',
                            padding: '0.9rem 1.2rem',
                            borderRadius: '1.2rem',
                            backgroundColor: isOutgoing ? GRAY_900 : senderKind === 'agent' ? AGENT_BUBBLE_BG : BEIGE_500,
                            color: isOutgoing ? WHITE : GRAY_900,
                          }}
                        >

                          {!!message.content &&
                            <Typography
                              variant="text-md"
                              sx={{ color: 'inherit', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                            >
                              {message.content}
                            </Typography>
                          }

                          {!!message.attachments?.length &&
                            <Box sx={{ marginTop: message.content ? '0.6rem' : 0 }}>
                              <MessageAttachments
                                attachments={message.attachments}
                                isOutgoing={isOutgoing}
                                onImageError={attachmentUrls.onImageError}
                                onImageLoad={handleAttachmentImageLoad}
                                onOpenFile={attachmentUrls.onOpenFile}
                              />
                            </Box>
                          }

                          <Stack
                            direction="row"
                            alignItems="center"
                            gap="0.4rem"
                            justifyContent={isOutgoing ? 'flex-end' : 'flex-start'}
                            sx={{ marginTop: '0.4rem' }}
                          >
                            <Typography
                              variant="text-xs"
                              sx={{ color: isOutgoing ? 'rgba(255,255,255,0.7)' : GRAY_700 }}
                            >
                              {format(new Date(message.createdAt), 'HH:mm')}
                            </Typography>

                            {isOutgoing && renderOutgoingStatus(capturedByAssistant ? ConversationMessageStatus.READ : message.status)}

                          </Stack>

                        </Stack>

                      </Stack>

                    </Stack>

                    {isAssistant && (message.id === rateableAnswerId || !!ratingOf(message) || !!retryRatingOf(message)) &&
                      <Stack direction="row" gap="0.8rem" sx={{ marginTop: '-0.4rem' }}>

                        <Box sx={{ width: '3.4rem', flexShrink: 0 }} />

                        <Box sx={{ flex: 1, maxWidth: '85%', minWidth: 0 }}>
                          <AnswerFeedback
                            rating={ratingOf(message)}
                            retryRating={retryRatingOf(message)}
                            disabled={rateAnswer.isPending}
                            onRate={(rating, confirmed) => void handleRateAnswer(message.id, rating, confirmed)}
                            onChoose={(rating) => handleChooseRating(message.id, rating)}
                            onDismissRetry={() => dropRating(setFailedRatings, message.id)}
                          />
                        </Box>

                      </Stack>
                    }

                    {isAssistant && !!message.actions?.length &&
                      <Stack direction="row" gap="0.8rem">

                        <Box sx={{ width: '3.4rem', flexShrink: 0 }} />

                        <Box sx={{ flex: 1, maxWidth: '85%', minWidth: 0 }}>
                          <MessageSuggestions
                            actions={message.actions}
                            selectedValue={selectedActions[message.id] ??
                              message.actions.find((action) => executedActionValues.has(action.value))?.value}
                            locked={!areSuggestionsLive}
                            onSelect={(action) => void handleSelectSuggestion(message.id, action)}
                          />
                        </Box>

                      </Stack>
                    }

                  </Fragment>
                )
              })}

              {showClosePrompt && !!latestMessage &&
                <Stack direction="row" gap="0.8rem">

                  <Box sx={{ width: '3.4rem', flexShrink: 0 }} />

                  <Stack gap="0.6rem" sx={{ flex: 1, maxWidth: '85%', minWidth: 0 }}>

                    <Typography variant="text-sm" sx={{ color: GRAY_700 }}>
                      {t('chat:thread.close_prompt.question')}
                    </Typography>

                    <MessageSuggestions
                      actions={[
                        { kind: 'reply', value: 'yes', label: t('chat:thread.close_prompt.yes') },
                        { kind: 'reply', value: 'no', label: t('chat:thread.close_prompt.no') },
                      ]}
                      onSelect={(action) => handleClosePrompt(latestMessage.id, action.value)}
                    />

                  </Stack>

                </Stack>
              }

              {isTypingShown &&
                <Stack
                  direction="row"
                  alignItems="flex-start"
                  gap="0.8rem"
                  data-testid="conversation-thread-typing"
                  sx={{
                    '@keyframes chat-typing-appear': {
                      from: { opacity: 0, transform: 'translateY(0.4rem)' },
                      to: { opacity: 1, transform: 'translateY(0)' },
                    },
                    animation: 'chat-typing-appear 0.2s ease-out',
                  }}
                >

                  {typingKind === 'other'
                    ? <Box sx={{ width: '3.4rem', flexShrink: 0 }} />
                    : <AgentAvatar kind={typingKind === 'assistant' ? 'assistant' : 'agent'} name={typingAgentName} />
                  }

                  <Box
                    sx={{
                      padding: '1.2rem 1.4rem',
                      borderRadius: '1.2rem',
                      backgroundColor: typingKind === 'agent' ? AGENT_BUBBLE_BG : BEIGE_500,
                    }}
                  >

                    <TypingDots />

                  </Box>

                </Stack>
              }

              <div ref={scrollAnchorRef} />
            </Stack>
        }
      </Box>

      <Box sx={{ borderTop: `1px solid ${GRAY_200}` }}>

        {isReadOnly &&
          <Stack alignItems="center" justifyContent="center" gap="1.2rem" sx={{ padding: '1.6rem 2rem' }}>
            <Typography variant="text-md" sx={{ color: GRAY_500, textAlign: 'center' }}>
              {t('chat:thread.closed_notice')}
            </Typography>
            {conversationType !== ConversationType.CLIENT_CREATIVE &&
              <StartConversationButton
                label={t('chat:new_conversation.start')}
                startIcon={<Add />}
                onClick={() => startNewConversation(conversationType)}
              />
            }
          </Stack>
        }

        {!isReadOnly && isWaitingForHumanAgent &&
          <SupportClosedNotice surface="thread" conversationId={conversationId} conversationType={conversationType} />
        }

        {!isReadOnly && isAssistantConversation && !isHandedToHuman &&
          <AIDisclaimer />
        }

        {!isReadOnly &&
          <MessageInput
            key={conversationId}
            placeholder={t('chat:input.placeholder')}
            submitting={sendMessage.isPending}
            autoFocus
            onSubmit={handleSubmit}
            onTyping={handleTyping}
            onAttachFromComputer={attachments.addFiles}
            attachments={attachments}
            sendAriaLabel={t('chat:actions.send')}
            attachAriaLabel={t('chat:actions.attach')}
          />
        }
      </Box>

    </Stack>
  )
}
